import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import dayjs from 'dayjs';
import { Table, Spin, Empty, Button, Select as AntSelect, Pagination } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSyncAlt } from '@fortawesome/pro-duotone-svg-icons';
import { Bar, Chart } from 'react-chartjs-2';
import { Chart as ChartJS } from 'chart.js';
import { MatrixController, MatrixElement } from 'chartjs-chart-matrix';
import { notificationsAtom } from '../../../../atoms/Atoms';
import {
  DAYS_WINDOW_OPTIONS,
  getPipelineHealthDomains,
  getPipelineHealthDomainDeepDive,
  getPipelineHealthSources
} from '../../../../api/pipelineHealthApi';
import {
  DOMAIN_COLORS,
  FALLBACK_COLOR,
  formatDomain,
  scoreColor,
  heatmapCellColor,
  DISTRIBUTION_BUCKETS
} from '../pipelineHealthUtils';
import { thousand } from '../../../../functions';

// Register the matrix controller + element once. Registration is idempotent,
// so it's safe if /dev/heatmap-test (or another component) has already done it.
ChartJS.register(MatrixController, MatrixElement);

// Tiny inline-SVG sparkline used in the master table. Normalises score
// values to 0–100 height range.
function Sparkline({ values = [], width = 110, height = 26, stroke = '#2563eb' }) {
  const points = values.map((v) => (v == null ? null : Math.max(0, Math.min(100, Number(v)))));
  if (points.every((p) => p == null)) {
    return <span className="text-gray-300">—</span>;
  }
  const step = points.length > 1 ? width / (points.length - 1) : 0;
  const segments = [];
  let runStart = null;
  for (let i = 0; i < points.length; i += 1) {
    const v = points[i];
    if (v == null) {
      if (runStart !== null) {
        // Capture runStart in a const so the inner map closure can't see
        // any later mutation of the outer-scope variable.
        const start = runStart;
        segments.push(
          points.slice(start, i)
            .map((vv, idx) => `${(start + idx) * step},${height - (vv / 100) * height}`)
            .join(' ')
        );
        runStart = null;
      }
    } else if (runStart === null) {
      runStart = i;
    }
  }
  if (runStart !== null) {
    const start = runStart;
    segments.push(
      points.slice(start)
        .map((vv, idx) => `${(start + idx) * step},${height - (vv / 100) * height}`)
        .join(' ')
    );
  }
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      {segments.map((pts) => (
        <polyline key={pts} points={pts} fill="none" stroke={stroke} strokeWidth="1.5" />
      ))}
    </svg>
  );
}

function DomainDeepDive() {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [data, setData] = useState(null);
  const [loadingDomains, setLoadingDomains] = useState(true);
  const [loadingData, setLoadingData] = useState(true);
  const [daysWindow, setDaysWindow] = useState(30);
  const [sources, setSources] = useState([]);
  const [sourceFilter, setSourceFilter] = useState('');
  const [entitiesPage, setEntitiesPage] = useState(1);

  const pushError = (description) => {
    setNotifications([...notifications, { title: t('error'), description, error: true }]);
  };

  // Fetch the domain list once on mount and seed the selector with the first
  // entry alphabetically.
  useEffect(() => {
    setLoadingDomains(true);
    getPipelineHealthDomains()
      .then((res) => {
        const list = res?.data?.data ?? [];
        setDomains(list);
        if (list.length > 0) setSelectedDomain(list[0]);
      })
      .catch((err) => pushError(err?.response?.data?.message || err.message))
      .finally(() => setLoadingDomains(false));
  }, []);

  // Sources feed the sourceFilter dropdown.
  useEffect(() => {
    getPipelineHealthSources()
      .then((res) => setSources(res?.data?.data ?? []))
      .catch((err) => pushError(err?.response?.data?.message || err.message));
  }, []);

  const loadDeepDive = (domain) => {
    if (!domain) return;
    setLoadingData(true);
    getPipelineHealthDomainDeepDive(domain, daysWindow, sourceFilter)
      .then((res) => {
        if (res?.data?.success === false) {
          pushError(res?.data?.error || t('error'));
          return;
        }
        setData(res?.data?.data ?? res?.data);
      })
      .catch((err) => pushError(err?.response?.data?.message || err.message))
      .finally(() => setLoadingData(false));
  };

  useEffect(() => {
    if (selectedDomain) loadDeepDive(selectedDomain);
  }, [selectedDomain, daysWindow, sourceFilter]);

  // Reset entities pagination whenever the filter set changes — keeps the
  // user from landing on a page that no longer exists.
  useEffect(() => { setEntitiesPage(1); }, [selectedDomain, daysWindow, sourceFilter]);

  const daysOptions = DAYS_WINDOW_OPTIONS.map((d) => ({
    value: d,
    label: t(`pipeline_health_days_${d}`)
  }));
  const sourceOptions = [
    { value: '', label: t('pipeline_health_all_sources') },
    ...sources?.map((s) => ({ value: s.source, label: formatDomain(s.source) }))
  ];

  // The domain picker is narrowed to the selected source's domains. With no
  // source filter we use the canonical /domains list.
  const availableDomains = useMemo(() => {
    if (sourceFilter) {
      const src = sources.find((s) => s.source === sourceFilter);
      return src?.domains || [];
    }
    return domains;
  }, [sources, sourceFilter, domains]);

  // If the user picks a source whose domains don't include the currently-
  // selected one, fall back to the first available domain for that source.
  useEffect(() => {
    if (availableDomains.length === 0) return;
    if (!selectedDomain || !availableDomains.includes(selectedDomain)) {
      setSelectedDomain(availableDomains[0]);
    }
  }, [availableDomains, selectedDomain]);

  // ---- Heatmap ----------------------------------------------------------

  const heatmapDateLabels = useMemo(() => {
    const rows = data?.heatmap30d || [];
    if (rows.length === 0) return [];
    return (rows[0].points || []).map((p) => dayjs(p.date).format('MM-DD'));
  }, [data]);

  const heatmapEntityLabels = useMemo(
    () => (data?.heatmap30d || []).map((r) => r.entityName),
    [data]
  );

  const matrixData = useMemo(() => {
    const rows = data?.heatmap30d || [];
    const out = [];
    rows.forEach((row) => {
      (row.points || []).forEach((p) => {
        out.push({
          x: dayjs(p.date).format('MM-DD'),
          y: row.entityName,
          v: p.healthScore == null ? null : Number(p.healthScore)
        });
      });
    });
    return out;
  }, [data]);

  const heatmapData = useMemo(() => ({
    datasets: [{
      label: t('pipeline_health_heatmap_30d'),
      data: matrixData,
      backgroundColor: (ctx) => heatmapCellColor(ctx.raw?.v),
      borderColor: 'rgba(255,255,255,0.6)',
      borderWidth: 1,
      // Cell sizing — fill the chart area divided by the axis cardinality.
      width: ({ chart }) => ((chart.chartArea || {}).width || 0) / Math.max(heatmapDateLabels.length, 1) - 2,
      height: ({ chart }) => ((chart.chartArea || {}).height || 0) / Math.max(heatmapEntityLabels.length, 1) - 2
    }]
  }), [matrixData, heatmapDateLabels.length, heatmapEntityLabels.length, t]);

  const heatmapOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: () => '',
          label: (ctx) => {
            const { x, y, v } = ctx.raw || {};
            return `${y} @ ${x}: ${v == null ? t('no_data') : Math.round(v)}`;
          }
        }
      }
    },
    scales: {
      x: {
        type: 'category',
        labels: heatmapDateLabels,
        offset: true,
        grid: { display: false },
        ticks: { autoSkip: true, maxTicksLimit: 10, font: { size: 9 } }
      },
      y: {
        // Reverse so entities appear top-to-bottom in source order.
        type: 'category',
        labels: [...heatmapEntityLabels].reverse(),
        offset: true,
        grid: { display: false },
        ticks: { font: { size: 10 } }
      }
    }
  }), [heatmapDateLabels, heatmapEntityLabels, t]);

  // Height grows with the number of entity rows so they each get ~18 px.
  const heatmapHeight = Math.max(280, heatmapEntityLabels.length * 22 + 60);

  // ---- Distribution bar chart ------------------------------------------

  const distributionChart = useMemo(() => {
    const dist = data?.distribution || {};
    return {
      labels: DISTRIBUTION_BUCKETS.map((b) => t(b.label)),
      datasets: [
        {
          label: t('pipeline_health_count'),
          data: DISTRIBUTION_BUCKETS.map((b) => dist[b.key] ?? 0),
          backgroundColor: DISTRIBUTION_BUCKETS.map((b) => b.color)
        }
      ]
    };
  }, [data, t]);

  const distributionOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      y: {
        beginAtZero: true,
        callback: (value, index, ticks) => thousand(value),
        ticks: { precision: 0 } }
    }
  };

  // ---- Master table ----------------------------------------------------

  const entitiesColumns = [
    { title: t('entity'), dataIndex: 'entityName', key: 'entityName' },
    {
      title: t('pipeline_health_current_score'),
      dataIndex: 'currentScore',
      key: 'currentScore',
      width: 130,
      render: (v) => (
        <span style={{ color: scoreColor(v), fontWeight: 600 }}>
          {v === null || v === undefined ? '—' : Number(v).toFixed(0)}
        </span>
      )
    },
    {
      title: t('pipeline_health_sparkline_7d'),
      dataIndex: 'sparkline7d',
      key: 'sparkline7d',
      width: 140,
      render: (vals, row) => (
        <Sparkline
          values={vals || []}
          stroke={DOMAIN_COLORS[data?.domain] || FALLBACK_COLOR}
        />
      )
    },
    {
      title: t('pipeline_health_days_with_data'),
      dataIndex: 'daysWithData',
      key: 'daysWithData',
      width: 130,
      render: thousand
    }
  ];

  // -----------------------------------------------------------------------

  const heatmap = data?.heatmap30d || [];
  const entities = data?.entities || [];
  const dist = data?.distribution || {};
  const distTotal = DISTRIBUTION_BUCKETS.reduce((s, b) => s + (dist[b.key] ?? 0), 0);

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-wrap items-center gap-3 px-2 pb-2">
        <span className="text-sm text-gray-600">{t('pipeline_health_source_filter')}:</span>
        <AntSelect
          value={sourceFilter}
          disabled={loadingData}
          onChange={setSourceFilter}
          style={{ minWidth: 160 }}
          options={sourceOptions}
        />
        <span className="text-sm text-gray-600">{t('domain')}:</span>
        <AntSelect
          value={selectedDomain}
          onChange={setSelectedDomain}
          loading={loadingDomains}
          disabled={loadingData || loadingDomains}
          style={{ minWidth: 200 }}
          options={availableDomains.map((d) => ({ value: d, label: formatDomain(d) }))}
        />
        <span className="text-sm text-gray-600">{t('pipeline_health_days_window')}:</span>
        <AntSelect
          value={daysWindow}
          disabled={loadingData}
          onChange={setDaysWindow}
          style={{ width: 150 }}
          options={daysOptions}
        />
        {data?.datasetStart && data?.datasetEnd && (
          <span className="text-sm text-gray-500">
            {dayjs(data.datasetStart).format('MMM DD, YYYY')} - {dayjs(data.datasetEnd).format('MMM DD, YYYY')}
          </span>
        )}
        {data?.datasetStart && !data?.datasetEnd && (
          <span className="text-sm text-gray-500">
            {dayjs(data.datasetStart).format('MMM DD, YYYY')} - {dayjs(data.datasetStart).add(30, 'days').format('MMM DD, YYYY')}
          </span>
        )}
        {!data?.datasetStart && data?.datasetEnd && (
          <span className="text-sm text-gray-500">
            {dayjs(data.datasetEnd).add(-30, 'days').format('MMM DD, YYYY')} - {dayjs(data.datasetEnd).format('MMM DD, YYYY')}
          </span>
        )}
        <Button
          className="ml-auto"
          type="primary"
          onClick={() => loadDeepDive(selectedDomain)}
          loading={loadingData}
          disabled={!selectedDomain}
          icon={<FontAwesomeIcon icon={faSyncAlt} />}
        >
          {t('refresh')}
        </Button>
      </div>

      {loadingData && !data ? (
        <div className="flex items-center justify-center py-10"><Spin /></div>
      ) : !data || !selectedDomain ? (
        <Empty description={t('no_data')} />
      ) : (
        <div className="px-2 pb-4 space-y-4">

          {/* Header card */}
          <Spin spinning={loadingData}>
            <div
              className="bg-white rounded shadow-sm p-3 flex flex-wrap items-center gap-6"
              style={{ borderLeft: `4px solid ${DOMAIN_COLORS[data.domain] || FALLBACK_COLOR}` }}
            >
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500">{t('domain')}</div>
                <div className="text-xl font-bold">{formatDomain(data.domain)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">{t('pipeline_health_avg_health')}</div>
                <div className="text-2xl font-bold" style={{ color: scoreColor(Number(data.avgHealth)) }}>
                  {data.avgHealth != null ? Number(data.avgHealth).toFixed(1) : '—'}
                </div>
              </div>
              <div className="flex gap-3 text-sm">
                <span><span className="text-green-600">●</span> {thousand(data.okCount)} {t('healthy')}</span>
                <span><span className="text-amber-600">●</span> {thousand(data.warnCount)} {t('warning')}</span>
                <span><span className="text-red-600">●</span> {thousand(data.critCount)} {t('critical')}</span>
              </div>
            </div>
          </Spin>
          {/* Heatmap + distribution side-by-side on large screens */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

            {/* Heatmap — 2/3 width on large screens. Card is a fixed 500px
                tall; the chart canvas grows by entity count and the middle
                area scrolls vertically so cells stay readable on long
                domains. Title + legend stay pinned. */}
            <div
              className="lg:col-span-2 bg-white rounded shadow-sm p-3 flex flex-col min-w-0 overflow-hidden"
            >
              <div className="text-sm font-semibold mb-2 flex-shrink-0">{t('pipeline_health_heatmap_30d')}</div>
              {heatmap.length === 0 ? (
                <Empty description={t('no_data')} />
              ) : (
                <>
                  <div className="flex-1 min-h-0 overflow-y-auto">
                    <Spin spinning={loadingData}>
                      <div style={{ height: heatmapHeight, position: 'relative', width: '100%' }}>
                        <Chart type="matrix" data={heatmapData} options={heatmapOptions} />
                      </div>
                    </Spin>
                  </div>
                  {/* Legend */}
                  <div className="flex gap-3 mt-2 text-[10px] text-gray-500 items-center flex-wrap flex-shrink-0">
                    {[1, 2, 3].map((threshold) => (
                      <span key={threshold} className="flex items-center gap-1">
                        <span style={{ background: heatmapCellColor(threshold), width: 10, height: 10, borderRadius: 2, display: 'inline-block' }} />
                        {threshold === 1 && t('healthy')}
                        {threshold === 2 && t('warning')}
                        {threshold === 3 && t('critical')}
                      </span>
                    ))}
                    <span className="flex items-center gap-1">
                      <span style={{ background: heatmapCellColor(null), width: 10, height: 10, borderRadius: 2, display: 'inline-block' }} />
                      {t('no_data')}
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* Distribution — same fixed height as the heatmap so the row
                stays aligned. Bar chart fills the remaining flex space. */}
            <div
              className="bg-white rounded shadow-sm p-3 flex flex-col min-w-0 overflow-hidden"
            >
              <div className="text-sm font-semibold mb-2 flex-shrink-0">{t('pipeline_health_distribution')}</div>
              {distTotal === 0 ? (
                <Empty description={t('no_data')} />
              ) : (
                <div className="flex-1 min-h-0 relative">
                  <div style={{ height: heatmapHeight, width: '100%', position: 'relative' }}>
                    <Bar data={distributionChart} options={distributionOptions} />
                  </div>
                  {loadingData && (
                    <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.6)' }}>
                      <Spin />
                    </div>
                  )}
                </div>
              )}
            </div>

          </div>

          {/* Entity Trend table */}
          <div className="bg-white rounded shadow-sm p-3">
            <div className="text-sm font-semibold mb-2">{t('pipeline_health_entity_table')}</div>
            {entities.length === 0 ? (
              <Empty description={t('no_data')} />
            ) : (
              <>
                <Table
                  size="small"
                  loading={loadingData}
                  rowKey={(row, idx) => `${row.entityName}-${idx}`}
                  columns={entitiesColumns}
                  dataSource={entities}
                  pagination={{ hideOnSinglePage: true }}
                />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default DomainDeepDive;
